import {
  API_ENDPOINT,
  API_GATEWAY_ENDPOINT,
  CLOUD_FRONT_ABSOLUTE_ENDPOINT,
  CODEBUILD_BUILD_NUMBER,
  CODEBUILD_GIT_SHORT_ID,
  LOCAL_ONLY_DEVELOPMENT,
} from './api-config';
import axios from 'axios';
import { addDays, subDays, startOfDay, endOfDay } from 'date-fns';


// https://daveceddia.com/multiple-environments-with-react/

import log from 'loglevel';
import { getSymbol } from './components/Symbols';


async function doGet(url = '') {
  const response = await fetch(url);
  if (!response.ok) {
    let error = {
      status: response.status,
      statusText: response.statusText
    };
    error.responseBody = await response.text();
    log.warn("doGet()", error);
    throw error;
  }
  return await response.json();
};

export async function fetchEarnings(
    last_evaluated_key=null,
    event_id=null,
    filter_condition=null) {
  let queryParams = "";
  if (last_evaluated_key) {
    queryParams = `last_evaluated_key=${last_evaluated_key}`;
  }
  if (event_id) {
    queryParams = `event_id=${event_id}`;
  }
  if (filter_condition) {
    if (!!queryParams) {
      queryParams += `&${filter_condition}`;
    } else {
      queryParams = filter_condition;
    }
  }

  const url = `${API_ENDPOINT}/earnings?${queryParams}`;
  log.debug("loading url", url);
  const response = await fetch(url);
  if (!response.ok) {
    log.warn("Response Not OK:", response);
    throw response;
  }
  return response.json();
};

function authHeaderPair(xAuthType, xAuthToken) {
  return {
    "x-auth-type": xAuthType,
    "x-auth-token": xAuthToken
  };
};

function extractAuthHeaders({ context }) {
  const { sessionContext } = context || {};
  const { session, google, test, appBuilderKey, customAuth, loginWithEmail } = sessionContext || {};
  if (session) {
    return authHeaderPair("session", session.session_id);
  } else if (google) {
    return authHeaderPair("google-web", google.tokenId);
  } else if (customAuth) {
    return authHeaderPair("custom", btoa(JSON.stringify(customAuth)));
  } else if (test) {
    return authHeaderPair("test", test.testAuthToken);
  } else if (appBuilderKey) {
    return authHeaderPair("app-builder", appBuilderKey);
  } else if (loginWithEmail) {
    return authHeaderPair("email", btoa(JSON.stringify(loginWithEmail)));
  }
  return {};
};

function getRequestHeaders(params) {
  return {
    ...extractAuthHeaders(params),
    "x-client-build-version": CODEBUILD_BUILD_NUMBER,
    "x-client-commit-id": CODEBUILD_GIT_SHORT_ID,
    "x-client-device-type": "web"
  };
};

export async function doAxiosGet(params) {
  return await doAxiosRequest("get", params)
};

export async function doAxiosPost(params) {
  return await doAxiosRequest("post", params)
};

export async function doAxiosDelete(params) {
  return await doAxiosRequest("delete", params)
};

export async function doAxiosRequest(method, params) {
  const {
    url,
    queryParams = {},
    data = {},
    cancelToken,
  } = params;
  // log.debug("loading url", url, queryParams, data, params);
  try {
    const { data: result } = await axios({
      method,
      url,
      params: queryParams,
      data,
      headers: getRequestHeaders(params),
      cancelToken,
    });
    return result;
  } catch (error) {
    // https://github.com/axios/axios#handling-errors
    log.error(`doAxiosRequest() failed for ${method}:${url} ${JSON.stringify(queryParams)} ${data}`);
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      log.error(error.response.data);
      log.error(error.response.status);
      log.error(error.response.headers);
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      log.error(error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      log.error('Error', error.message);
    }
    throw error;
  }
};

export async function getECSBuildTasks(params) {
  return doAxiosGet({ url: `${API_ENDPOINT}/build/tasks`, ...params });
};

export async function getMobileAppBuilds(params) {
  return doAxiosGet({ url: `${API_ENDPOINT}/mobile/builds`, ...params });
};

export async function getNews(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/news`, ...params });
};

export async function userAuthSetup(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/user/auth/setup`, ...params });
};

export async function postNews(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/news`, ...params });
};

export async function getEarnings(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/earnings`, ...params });
};

export async function newEarnings(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/earnings`, ...params });
}

export async function deleteEarnings(params) {
  return await doAxiosDelete({ url: `${API_ENDPOINT}/earnings`, ...params });
};

export async function recordEarnings(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/earnings/record`, ...params });
};

export async function buildWebApp(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/build/app`, ...params });
};

export async function transcribeEarnings(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/earnings/transcribe`, ...params });
};

export async function notifyUsersEventPublished(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/earnings/notify`, ...params });
};

export async function subscribeEmail(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/subscribe/email`, ...params });
};

export async function getProfile(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/profile`, ...params });
};

export async function saveProfile(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/profile`, ...params });
};

export async function deleteProfile(params) {
  return await doAxiosDelete({ url: `${API_ENDPOINT}/profile`, ...params });
};

export async function fetchCompany(exhcange, symbol) {
  const url = `${API_ENDPOINT}/company?exchange=${exhcange}&symbol=${symbol}`;
  return doGet(url);
};

export async function getCompany(params) {
  const suffix = LOCAL_ONLY_DEVELOPMENT ? ".json" : "";
  return doAxiosGet({ url: `${API_ENDPOINT}/company${suffix}`, ...params });
};

export async function updateCompany(params) {
  return doAxiosPost({ url: `${API_ENDPOINT}/company`, ...params });
};

export async function getSiteStats(appBuilderKey, params) {
  const suffix = LOCAL_ONLY_DEVELOPMENT ? ".json" : "";
  let res = await doAxiosGet({
    url: `${API_GATEWAY_ENDPOINT}/site/stats${suffix}`,
    context: {
      sessionContext: {
        appBuilderKey,
      },
    },
    ...params,
  });
  // console.log("Site stats:", res);
  return res;
};

export const earningsQuery = async (queryParams, appBuilderKey) => {
  const suffix = LOCAL_ONLY_DEVELOPMENT ? ".json" : "";
  const results = await getEarnings({
    url: `${API_GATEWAY_ENDPOINT}/earnings${suffix}`,
    queryParams: {
      params: JSON.stringify(queryParams),
    },
    context: {
      sessionContext: {
        appBuilderKey,
      },
    },
  });
  // console.log("earningsResults:", results);
  return results.earnings.map(({ quarter, year, exchange, symbol, primary_conference_date }) => {
    return {
      exchange,
      quarter: quarter.toUpperCase(),
      year,
      symbol,
      conference_date: primary_conference_date,
    };
  });
};

const earningsWithCompanyName = async (queryParams, appBuilderKey) => {
  const result = await earningsQuery(queryParams, appBuilderKey);
  // console.log("earningsWithCompanyName result:", result);
  const symbolsConfig = {
    endpoint: CLOUD_FRONT_ABSOLUTE_ENDPOINT,
    symbolsFile: "all-symbols.json",
  };
  const allResults = await Promise.all(
    result.map(async ({ quarter, year, exchange, symbol, conference_date }) => {
      // console.log("symbol:", symbol);
      let _sym = await getSymbol(exchange, symbol, symbolsConfig);
      // console.log("_sym", _sym);
      const { name = null } = _sym || {};
      if (!name) {
        log.error(`Error getting company name for: ${exchange}_${symbol}`);
      }
      // console.log("name:", name, "symbol:", symbol);
      return {
        quarter,
        year,
        exchange,
        symbol,
        companyName: name,
        conference_date,
      };
    })
  );
  return allResults.filter(result => result.companyName);  // Return only results that have companyName set
};

export const getRecentCalls = async (appBuilderKey) => {

  const queryParams = {
    sort_by: "recording_stop_date",
    fields_present: [
      "audio_file"
    ],
    fields_not_present: [],
    match: {
      is_published: true,
      primary: true,
    },
    size: 5,
  };
  log.debug("getRecentCalls()", queryParams);
  return await earningsWithCompanyName(queryParams, appBuilderKey);
};

export const earningsCallsQueryParams = () => {
  return {
    sort_by: "primary_conference_date",
    sort_order: "asc",
    fields_present: [
      "exchange",
      "symbol",
      "primary_conference_date",
      "year",
      "quarter",
    ],
    fields_not_present: [],
    match: {
      primary: true,
    },
    not_match: {
      news_wire: "test",
    },
    filters: [hasConferenceFilter()],
    size: 50,
  };
};

const hasConferenceFilter = () => {
  return {
    bool: {
      should: [
        {
          match_phrase: {
            has_teleconference: true
          }
        },
        {
          match_phrase: {
            has_web_cast: true
          }
        }
      ],
      minimum_should_match: 1
    }
  };
};

export const getUpcomingCalls = async (appBuilderKey) => {
  log.debug("getUpcomingCalls()");
  const dateRangeFilter = {
    primary_conference_date: {
      "format": "strict_date_optional_time",
      "gte": new Date().toISOString(),
    },
  };
  const queryParams = {
    ...earningsCallsQueryParams(),
    range_filter: [dateRangeFilter],
  };
  return await earningsWithCompanyName(queryParams, appBuilderKey);
};

export const getEarningsCalendar = async ({ startingDate = new Date(), daysBack = 30, daysForward = 30, appBuilderKey }) => {
  // console.log("earningsCalendar()");
  const dateRangeFilter = {
    primary_conference_date: {
      "format": "strict_date_optional_time",
      "gte": startOfDay(subDays(startingDate, daysBack)).toISOString(),
      "lte": endOfDay(addDays(startingDate, daysForward)).toISOString(),
    },
  };
  const queryParams = {
    ...earningsCallsQueryParams(),
    range_filter: [dateRangeFilter],
    size: 250,
  };
  return await earningsWithCompanyName(queryParams, appBuilderKey);
};

export async function identifyDevice(params) {
  return doAxiosGet({url: `${API_ENDPOINT}/id`, ...params});
};

export async function newSession(params) {
  return doAxiosGet({url: `${API_ENDPOINT}/login`, ...params});
};

export async function getTranscript(transcription_file, context) {
  // console.log(" -> getTranscript:", transcription_file);
  return await doAxiosGet({
    url: `${API_GATEWAY_ENDPOINT}/transcript`,
    queryParams: {
      transcription_file,
    },
    context,
  });
};

export async function recordAnalyticsEvents(params) {
  return await doAxiosPost({url: `${API_ENDPOINT}/tk`, ...params});
};

export async function pageLoadEvent(params) {
  return await doAxiosPost({url: `${API_ENDPOINT}/pg`, ...params});
};
